import LocalizationStorage from '%app/js/common/services/localizationStorage';
import logger from '%app/js/common/services/Logger';

const MAPS_SCRIPT_URL = 'https://maps.googleapis.com/maps/api/js';
const READY_CALLBACK_NAME = '__searchPanelPlacesLibraryReady';
const AUTH_FAILURE_HANDLER_NAME = 'gm_authFailure';
const SLOW_LOAD_REPORT_DELAY_MS = 20000;
const MAX_LOAD_ATTEMPTS = 2;

const UNUSABLE_REASONS = Object.freeze({
    keyRejected: 'key_rejected',
});

const UNUSABLE_MESSAGES = Object.freeze({
    [UNUSABLE_REASONS.keyRejected]: 'placesLibraryLoader: Google rejected the Maps key',
});

function isUsablePlacesLibrary(library) {
    return Boolean(library)
        && typeof library.AutocompleteService === 'function'
        && Boolean(library.PlacesServiceStatus);
}

function readLoadedMaps() {
    return window.google && window.google.maps ? window.google.maps : null;
}

function buildScriptUrl(apiKey) {
    const params = new URLSearchParams({
        key: apiKey,
        libraries: 'places',
        language: LocalizationStorage.getGoogleLanguage(),
        callback: READY_CALLBACK_NAME,
    });

    return `${MAPS_SCRIPT_URL}?${params.toString()}`;
}

function reportSlowLoad() {
    if (document.visibilityState !== 'visible') {
        return;
    }
    logger.warning('placesLibraryLoader: Google Maps is taking too long to load', {
        reason: 'slow', waitedMs: SLOW_LOAD_REPORT_DELAY_MS,
    });
}

function withSlowLoadReport(loading) {
    const timer = setTimeout(reportSlowLoad, SLOW_LOAD_REPORT_DELAY_MS);

    return loading.then((library) => {
        clearTimeout(timer);
        return library;
    });
}

export function createPlacesLibraryLoader() {
    const unusableListeners = new Set();
    let pendingLoad = null;
    let attempts = 0;
    let unusableReason = null;
    let partialLoadReported = false;
    let quotaExceededReported = false;
    let authFailureHandlerInstalled = false;

    function reportPartialLoad() {
        if (!partialLoadReported) {
            partialLoadReported = true;
            logger.warning('placesLibraryLoader: Google Maps arrived without place search', {
                reason: 'partial',
            });
        }
    }

    function importPlacesLibrary(maps) {
        if (typeof maps.importLibrary !== 'function') {
            reportPartialLoad();
            return Promise.resolve(null);
        }

        return maps.importLibrary('places').then(
            (library) => {
                if (isUsablePlacesLibrary(library)) {
                    return library;
                }
                reportPartialLoad();
                return null;
            },
            () => null,
        );
    }

    function resolveArrivedPlacesLibrary() {
        const maps = readLoadedMaps();
        if (maps === null) {
            reportPartialLoad();
            return Promise.resolve(null);
        }

        return isUsablePlacesLibrary(maps.places) ? Promise.resolve(maps.places) : importPlacesLibrary(maps);
    }

    function markUnusable(reason) {
        if (unusableReason !== null) {
            return;
        }
        unusableReason = reason;
        logger.error(UNUSABLE_MESSAGES[reason], { reason });
        unusableListeners.forEach((listener) => {
            listener();
        });
    }

    function reportQuotaExceeded() {
        if (!quotaExceededReported) {
            quotaExceededReported = true;
            logger.error('placesLibraryLoader: Google Places quota is exhausted', { reason: 'quota_exceeded' });
        }
    }

    function installAuthFailureHandler() {
        if (authFailureHandlerInstalled) {
            return;
        }
        authFailureHandlerInstalled = true;
        const previousHandler = window[AUTH_FAILURE_HANDLER_NAME];
        window[AUTH_FAILURE_HANDLER_NAME] = () => {
            markUnusable(UNUSABLE_REASONS.keyRejected);
            if (typeof previousHandler === 'function') {
                previousHandler();
            }
        };
    }

    function injectScript(apiKey) {
        installAuthFailureHandler();

        return new Promise((resolve) => {
            const script = document.createElement('script');
            let settled = false;
            const settleWith = (resolvePlacesLibrary) => {
                if (!settled) {
                    settled = true;
                    resolve(resolvePlacesLibrary());
                }
            };
            const abandon = () => {
                delete window[READY_CALLBACK_NAME];
                script.remove();
                settleWith(() => null);
            };
            window[READY_CALLBACK_NAME] = () => {
                delete window[READY_CALLBACK_NAME];
                settleWith(resolveArrivedPlacesLibrary);
            };
            script.addEventListener('error', abandon);
            script.addEventListener('load', () => {
                const maps = readLoadedMaps();
                if (maps === null) {
                    abandon();
                } else if (typeof maps.importLibrary === 'function') {
                    settleWith(() => importPlacesLibrary(maps));
                }
            });
            script.async = true;
            script.src = buildScriptUrl(apiKey);
            document.head.appendChild(script);
        });
    }

    function load(apiKey) {
        if (unusableReason !== null) {
            return Promise.resolve(null);
        }
        const maps = readLoadedMaps();
        if (maps !== null && isUsablePlacesLibrary(maps.places)) {
            return Promise.resolve(maps.places);
        }
        if (pendingLoad !== null) {
            return pendingLoad;
        }
        if (!apiKey || attempts >= MAX_LOAD_ATTEMPTS) {
            return Promise.resolve(null);
        }
        attempts += 1;
        pendingLoad = withSlowLoadReport(maps === null ? injectScript(apiKey) : importPlacesLibrary(maps))
            .then((library) => {
                pendingLoad = null;
                return unusableReason === null ? library : null;
            });

        return pendingLoad;
    }

    function subscribe(listener) {
        unusableListeners.add(listener);

        return () => {
            unusableListeners.delete(listener);
        };
    }

    return {
        load, markUnusable, reportQuotaExceeded, subscribe,
    };
}

export {
    AUTH_FAILURE_HANDLER_NAME, READY_CALLBACK_NAME, SLOW_LOAD_REPORT_DELAY_MS, UNUSABLE_REASONS,
};

export default createPlacesLibraryLoader();
